import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SeatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (seats: number) => void;
  planName: string;
  price: number;
  isYearly: boolean;
}

export const SeatsModal = ({
  isOpen,
  onClose,
  onConfirm,
  planName,
  price,
  isYearly,
}: SeatsModalProps) => {
  const { t } = useTranslation('pricing');
  const [seats, setSeats] = useState(1);

  const handleIncrement = () => {
    setSeats(prev => prev + 1);
  };

  const handleDecrement = () => {
    if (seats > 1) {
      setSeats(prev => prev - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      setSeats(value);
    } else if (e.target.value === '') {
      setSeats(1);
    }
  };

  const handleConfirm = () => {
    onConfirm(seats);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('seats-modal.title')}
      className="max-w-md"
    >
      <div className="flex flex-col space-y-6">
        <div className="text-center">
          <Text className="text-lg font-medium text-gray-700">
            {t('seats-modal.subtitle', { planName })}
          </Text>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <Text className="text-sm text-gray-600">
              {t('seats-modal.price-per-seat')}:
            </Text>
            <Text className="font-medium text-secondary">
              {price}€ {isYearly ? t('per-month-yearly') : t('per-month')}
            </Text>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <Text className="text-sm text-gray-600">
              {t('seats-modal.total-price')}:
            </Text>
            <Text className="font-bold text-secondary">
              {(price * seats).toFixed(2)}€{' '}
              {isYearly ? t('per-month-yearly') : t('per-month')}
            </Text>
          </div>
        </div>

        <div className="flex flex-col space-y-2">
          <Text className="text-sm font-medium text-gray-700">
            {t('seats-modal.select-seats')}
          </Text>
          <div className="flex items-center justify-center space-x-4">
            <Button
              onClick={handleDecrement}
              disabled={seats <= 1}
              className="flex size-12 items-center justify-center rounded-full bg-transparent px-4 text-lg font-semibold text-linko-dark shadow-none hover:text-linko-light hover:shadow-none"
              aria-label={t('seats-modal.decrease')}
            >
              -
            </Button>
            <input
              type="number"
              min="1"
              value={seats}
              onChange={handleInputChange}
              className="flex h-12 w-16 items-center justify-center rounded-md border border-gray-300 bg-white text-center text-xl font-bold text-secondary [appearance:textfield] focus:outline-none focus:ring-1 focus:ring-linko-light [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              aria-label={t('seats-modal.select-seats')}
            />
            <Button
              onClick={handleIncrement}
              className="flex size-12 items-center justify-center rounded-full bg-transparent px-3 text-lg font-semibold text-linko-dark shadow-none hover:text-linko-light hover:shadow-none"
              aria-label={t('seats-modal.increase')}
            >
              +
            </Button>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            onClick={onClose}
            className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {t('seats-modal.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-linko-light hover:bg-linko-dark"
          >
            {t('seats-modal.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
